import getLogger from '@ignitesales/logger-es';
import { buildPageUrl, setRuleEvaluatorAnswers, isDetailsPage } from '../utils';
import {
  extractPageFromPath,
  extractAnswersFromPageResponse,
  extractProgressFromPageResponse,
  extractProductsFromPageResponse,
  extractValidationErrorsFromPageResponse,
  extractAnswersWithPageInfoFromPageResponse,
} from '../state';
import deepmerge from 'deepmerge';
import {VALID_DB_ATTRIBUTE_KEYS} from '../utils/constants.mjs';
import { GuideApiService } from './GuideApiService.mjs';

const logger = getLogger({ module: 'ignite' });
const serviceWaitTimeout = 30000;
let runMode = 'development';

const getRunMode = () => runMode;

const setRunMode = (newRunMode) => {
  logger.info('Setting runMode', newRunMode);
  runMode = newRunMode;
};

const setFlashData = (key, value) => {
  if(!window?.ignite?.flashData) {
    window.ignite.flashData = {};
  }
  window.ignite.flashData[key] = value;
};

const getFlashData = (key, shouldPersist) => {
  let result = false;
  if(window?.ignite?.flashData && window.ignite.flashData[key]) {
    result = window.ignite.flashData[key];
    if (!shouldPersist) {
      delete window.ignite.flashData[key];
    }
  } else {
    logger.warn('Flash data key does not exists', key);
  }
  return result;
};

//Old way that mm-web lets front-end know that a user needs to login. It is also used to send up SSO employee info, which we still need
const showEmployeeDialog = (isLoggedIn, selectedEmployee) => {
  logger.debug('Deprecated employee login flow call triggered, neuro-app will handle this flow', {isLoggedIn, selectedEmployee});
  if (!isLoggedIn && selectedEmployee) {
    logger.debug('Found pre-selected employee information from mm-web, saving for use in neuro-app login flow');
    setFlashData('employeeLoginData', {isLoggedIn, selectedEmployee});
  }
};

const commonServicesInjected = () => {
  return (
    ignite &&
    ignite.answerService &&
    ignite.pageResolutionService &&
    ignite.contactService &&
    ignite.noteService &&
    ignite.appointmentService
  );
};

const branchServicesInjected = (isBranchGuide) => {
  if( isDetailsPage() || !isBranchGuide ) {
    logger.debug('No branch services needed');
  }
  return isDetailsPage() || !isBranchGuide || (ignite && ignite.employeeLoginService);
};

const servicesInitialized = () => {
  const waitStart = Date.now();
  return new Promise((resolve, reject) => {
    const checkForServices = () => {
      logger.info('checking service availability');
      if (commonServicesInjected()) {
        logger.info('services ready');
        return resolve(true);
      }

      const elapsed = Date.now() - waitStart;
      if (elapsed >= serviceWaitTimeout) {
        return reject(new Error('services unavailable'));
      }

      logger.info('waiting for service availability');
      setTimeout(() => checkForServices(), 200);
    };
    checkForServices();
  });
};

const loadInitialState = async (options = {}) => {
  await servicesInitialized();
  logger.info('loading initial state', options);

  // initialize user
  const initializedUser = await ignite.pageResolutionService.initializeUser();
  const { uuid, guideDelivery } = initializedUser;

  // determine if branch guide services were injected
  const isBranchGuide = guideDelivery.toLowerCase() === 'branch';
  if( branchServicesInjected(isBranchGuide) ) {
    logger.debug('All ignite services have been initialized for state setup');
  } else {
    logger.error('Unable to load all ignite services needed to support this guide type');
  }

  // load current page data
  const currentPageRequest = extractPageFromPath(window.location.pathname);
  const currentPageResponse = await ignite.pageResolutionService.loadPage({
    page: currentPageRequest,
  });
  const currentPageUrl = buildPageUrl({
    ...currentPageResponse.page,
    type: currentPageResponse.type,
    uuid,
  });
  const currentPageType = currentPageResponse.type;
  // get answers from page response and from local answer store
  const answers = extractAnswersFromPageResponse(currentPageResponse);
  const localAnswers = JSON.parse( window.sessionStorage.getItem(`${uuid}-localAnswers`) );
  setRuleEvaluatorAnswers(answers);
  // get answersWithPageInfo
  const answersWithPageInfo = extractAnswersWithPageInfoFromPageResponse(currentPageResponse);
  // get products, progress, and page validations
  const progress = extractProgressFromPageResponse(currentPageResponse);
  const products = extractProductsFromPageResponse(currentPageResponse);
  const validationErrors = extractValidationErrorsFromPageResponse(currentPageResponse);

  const guideDatabaseAttributes = await window.ignite.userService.getGuideAttributes();
  const guideAttributesForAppState = Object.fromEntries(
    Object.entries(guideDatabaseAttributes || {}).filter( ([key]) => VALID_DB_ATTRIBUTE_KEYS.includes(key) )
  );

  let paths = null;
  if ( window?.ignite?.pageResolutionService?.getPaths ) { //need to check here just in case mm-web version does not have this new function
    paths = await window.ignite.pageResolutionService.getPaths(); //TODO: need to implement mock service if this is needed during non-connected development
  }

  // noinspection UnnecessaryLocalVariableJS
  /**
   * @typedef NeuroStateInitObject
   * @property {Object} user - active user details
   * @property {string} currentPage - current page url
   * @property {Object} pages - guide pages that have been accessed
   * @property {Object} paths - list of paths that have been satisfied
   * @property {Object} answers - conversation answers
   * @property {Object} answersWithPageInfo - conversation answers with page info (i.e. pageKey and pathKey)
   * @property {Object} products - recommended products
   * @property {Object} progress - guide completion progress
   * @property {Array} validationErrors - validation errors for current page
   * @property {Object} guideConfigurations - object with guide config details (i.e. isBranchGuide, isMockMode, attributes, etc)
   * @type NeuroStateInitObject
   */
  const neuroState = {
    answers: {...localAnswers, ...answers },
    answersWithPageInfo,
    pages: {
      currentPageUrl,
      currentPageType,
      defaultPageTitle: document.title,
      progress,
      isAnimatingPages: false,
      isLoadingNextPage: false,
      savedPages: {
        [currentPageUrl]: currentPageResponse.page
      },
      validationErrors
    },
    paths,
    products,
    user: { ...initializedUser },
    guideConfigurations: {
      isBranchGuide,
      ...guideAttributesForAppState
    }
  };

  const initialState = deepmerge( options.preInitState || {}, neuroState );
  const decoratedState = options?.onStateInit ? await options.onStateInit(initialState) : null;
  const finalState = decoratedState ? decoratedState : initialState;

  GuideApiService.useUser(finalState.user);
  GuideApiService.useGuide(finalState.guideConfigurations);
  GuideApiService.useAnswers(finalState.answers);
  
  return finalState;
};

const ignite = {
  getFlashData,
  getRunMode,
  loadInitialState,
  servicesInitialized,
  setRunMode,
  setFlashData,
  showEmployeeDialog
};
window.ignite = ignite;

export {
  ignite as default,
  ignite,
  servicesInitialized
};
